import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';
import { RECEIVE } from '~/constants/errorCode';

import type { TypeApiResponse, TypeGetReceivingRequestCopyResponse } from '~/types/receive.types';

type CopyParams = {
  ownerCode: string;
  requestIds: string;
};

/**
 * [입고요청관리] 입고요청 복사 API
 */
const fetchReceivingRequestCopy = async ({ ownerCode, requestIds }: CopyParams) => {
  const { data } = await axiosInstance.get<TypeApiResponse<TypeGetReceivingRequestCopyResponse['data']>>(
    `${API_ENDPOINT.RECEIVE.GET_REQUEST_COPY}/${ownerCode}/request/copy`,
    { params: { requestIds } }
  );
  return data.data;
};

/**
 * [입고요청관리] 입고요청 복사 Mutation Hook
 * @description 에러 처리는 사용처에서 개별 처리 (특수 에러 코드별 dialog 표시)
 */
export const useGetReceivingRequestCopyMutation = () => {
  return useMutation({
    mutationFn: fetchReceivingRequestCopy,
    meta: { showGlobalErrorToast: false },
  });
};

// 명세 예시 응답: cluster CC02, warehouse GGH1, supplier SU000004
const normalResponse: TypeGetReceivingRequestCopyResponse = {
  data: {
    cluster: 'CC02',
    warehouse: 'GGH1',
    xdock: false,
    xdockCenter: 'GPH1',
    supplierCode: 'SU000004',
    supplierName: '테스트 화주사_첫번째 공급사',
    orderItems: [
      {
        goodsId: 173855,
        goodsCode: 'EN0000010151',
        alternativeCode: '880000009876',
        goodsName: '닥터유 단백질 35g(3PL)',
        kurlyKeepTypeDesc: '상온',
        shelfLifeType: '',
        dateUseYn: false,
        dateType: null,
        expirationDate: null,
        manufactureDate: null,
        usePallet: false,
        boxQty: 0,
        boxPerQty: 0,
        eachQty: 0,
        totalQty: 0,
        lotNo: null,
      },
      {
        goodsId: 173856,
        goodsCode: 'EN0000010152',
        alternativeCode: '8800000010152',
        goodsName: '닥터유 단백질 40g(3PL)',
        kurlyKeepTypeDesc: '상온',
        shelfLifeType: 'USE_DATE',
        dateUseYn: false,
        dateType: null,
        expirationDate: null,
        manufactureDate: null,
        usePallet: false,
        boxQty: 1,
        boxPerQty: 18,
        eachQty: 0,
        totalQty: 18,
        lotNo: null,
      },
    ],
  },
};

const copyWithNoticeResponse: TypeGetReceivingRequestCopyResponse = {
  data: {
    ...normalResponse.data,
    notice: {
      duplicateSku: '중복된 SKU는 1개 행으로 병합되었으며,\n기한 정보, LOT번호, 요청수량은\n초기화되었습니다.',
      deactivatedSku: '복사가 완료되었습니다.\n(사용 중단 상품 2건 제외)',
    },
  },
};

registerMockAPI<TypeApiResponse<TypeGetReceivingRequestCopyResponse['data']>>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/request/copy',
  label: '입고/입고요청/복사',
  scenarios: [
    { name: '정상 응답', response: normalResponse },
    { name: '정상 응답 — 안내 메세지 포함', response: copyWithNoticeResponse },
    {
      name: '수량 초과 (9020)',
      response: {
        error: {
          code: RECEIVE.SKU_AND_QUANTITY_COUNT_EXCEEDED,
          message:
            '선택하신 항목의 총합이 입고요청 품목 200개, \n입고 가능 수량 20,000개를 초과할 수 없습니다. 수량을 나누어 입고 요청 복사 작업을 \n진행해 주세요.',
        },
      },
      status: 400,
    },
    {
      name: '다른 창고 포함 (9065)',
      response: {
        error: {
          code: RECEIVE.DIFFERENT_WAREHOUSE_INCLUDED,
          message:
            '선택하신 항목 중 입고센터가 서로 다른 건이 포함되어 있습니다.\n입고센터별로 나누어 입고 요청 복사 작업을 진행해주세요.',
        },
      },
      status: 400,
    },
    {
      name: '다른 공급사 포함 (9066)',
      response: {
        error: {
          code: RECEIVE.DIFFERENT_SUPPLIER_INCLUDED,
          message:
            '선택하신 항목 중 공급사가 서로 다른 건이 포함되어 있습니다.\n동일 공급사별로 나누어 입고 요청 복사 작업을 진행해주세요.',
        },
      },
      status: 400,
    },
    {
      name: '다른 센터+공급사 포함 (9067)',
      response: {
        error: {
          code: RECEIVE.DIFFERENT_CENTER_AND_SUPPLIER_INCLUDED,
          message:
            '선택하신 항목 중 입고센터와 공급사 모두 서로 다른 건이 포함되어 있습니다.\n입고센터별, 공급사별로 나누어 입고 요청 복사 작업을 진행해 주세요.',
        },
      },
      status: 400,
    },
  ],
});
